// src/hooks/BroadcastFormReportHooks.ts

import useSWR, { SWRConfiguration, Fetcher } from 'swr'
import {
  useQueryParam,
  NumberParam,
  StringParam,
} from 'use-query-params'

import {
  IBroadcastFormMap,
  IEnterpriseBroadcastFormGeneratorMap,
  IBroadcastFormQuestionsByFormTypeMap,
  IBroadcastFormResponseFormatMap,
  IBroadcastFormResponseOptionsMap,
  IBroadcastFormRecipientResponseReportRow,
  IBroadcastFormReportGroup,
  IPageResult,
} from 'types/BroadcastForm'
import * as BroadcastFormReportAPI from 'api/BroadcastFormReportAPI'
import React from 'react'

// ------------------------------------------
// Generators
// ------------------------------------------

export const useBroadcastFormReportGenerators = (
  options: SWRConfiguration<
  IEnterpriseBroadcastFormGeneratorMap, any, Fetcher<IEnterpriseBroadcastFormGeneratorMap> | undefined
  > = {},
) => useSWR<IEnterpriseBroadcastFormGeneratorMap>(
  ['broadcast-form-report-generators'],
  () => BroadcastFormReportAPI.getBroadcastFormReportGenerators(),
  options,
)

// ------------------------------------------
// Forms (report scope)
// ------------------------------------------

export const useBroadcastFormReportForms = (
  options: SWRConfiguration<
  IBroadcastFormMap, any, Fetcher<IBroadcastFormMap> | undefined
  > = {},
) => useSWR<IBroadcastFormMap>(
  ['broadcast-form-report-forms'],
  () => BroadcastFormReportAPI.getBroadcastFormReportForms(),
  options,
)

// ------------------------------------------
// Questions by FormType
// ------------------------------------------

export const useBroadcastFormReportQuestions = (
  options: SWRConfiguration<
  IBroadcastFormQuestionsByFormTypeMap, any, Fetcher<IBroadcastFormQuestionsByFormTypeMap> | undefined
  > = {},
) => useSWR<IBroadcastFormQuestionsByFormTypeMap>(
  ['broadcast-form-report-questions'],
  () => BroadcastFormReportAPI.getBroadcastFormReportQuestions(),
  options,
)

// ------------------------------------------
// Response formats
// ------------------------------------------

export const useBroadcastFormReportResponseFormats = (
  options: SWRConfiguration<
  IBroadcastFormResponseFormatMap, any, Fetcher<IBroadcastFormResponseFormatMap> | undefined
  > = {},
) => useSWR<IBroadcastFormResponseFormatMap>(
  ['broadcast-form-report-response-formats'],
  () => BroadcastFormReportAPI.getBroadcastFormReportResponseFormats(),
  options,
)

// ------------------------------------------
// Response options
// ------------------------------------------

export const useBroadcastFormReportResponseOptions = (
  options: SWRConfiguration<
  IBroadcastFormResponseOptionsMap, any, Fetcher<IBroadcastFormResponseOptionsMap> | undefined
  > = {},
) => useSWR<IBroadcastFormResponseOptionsMap>(
  ['broadcast-form-report-response-options'],
  () => BroadcastFormReportAPI.getBroadcastFormReportResponseOptions(),
  options,
)

// ------------------------------------------
// Groups (for group filtering)
// ------------------------------------------

export const useBroadcastFormReportGroups = (
  options: SWRConfiguration<
  IBroadcastFormReportGroup[], any, Fetcher<IBroadcastFormReportGroup[]> | undefined
  > = {},
) => useSWR<IBroadcastFormReportGroup[]>(
  ['broadcast-form-report-groups'],
  () => BroadcastFormReportAPI.getBroadcastFormReportGroups(),
  options,
)

// ------------------------------------------
// Paged recipient responses
// ------------------------------------------
// Uses query params:
//   fromUtc  (optional, ISO string)
//   toUtc    (optional, ISO string)
//   page     (1-based, default 1)
//   pageSize (default 1000)
// ------------------------------------------

export const useBroadcastFormRecipientResponsesPage = (
  options: SWRConfiguration<
  IPageResult<IBroadcastFormRecipientResponseReportRow>, any,
  Fetcher<IPageResult<IBroadcastFormRecipientResponseReportRow>> | undefined
  > = {},
) => {
  const [fromUtcParam] = useQueryParam('fromUtc', StringParam)
  const [toUtcParam] = useQueryParam('toUtc', StringParam)
  const [pageParam = 1] = useQueryParam('page', NumberParam)
  const [pageSizeParam = 1000] = useQueryParam('pageSize', NumberParam)

  return useSWR<IPageResult<IBroadcastFormRecipientResponseReportRow>>(
    [
      'broadcast-form-report-recipient-responses',
      fromUtcParam,
      toUtcParam,
      pageParam,
      pageSizeParam,
    ],
    ([, fromUtc, toUtc, page, pageSize]) => BroadcastFormReportAPI.getBroadcastFormRecipientResponsesPage({
      fromUtc: fromUtc || undefined,
      toUtc: toUtc || undefined,
      page,
      pageSize,
    }),
    options,
  )
}

type UseAllRecipientResponsesResult = {
  data: IBroadcastFormRecipientResponseReportRow[]
  totalRecords: number
  isTruncated: boolean
  isLoading: boolean
  error: any
  loadedCount: number
  progressPercentage: number
}

/**
 * Loads recipient responses in pages (5000 rows at a time),
 * up to a maximum of 100,000 rows total.
 *
 * Uses query params:
 *   fromUtc  (optional, ISO string)
 *   toUtc    (optional, ISO string)
 */
export const useAllBroadcastFormRecipientResponses = (): UseAllRecipientResponsesResult => {
  const [fromUtcParam] = useQueryParam('fromUtc', StringParam)
  const [toUtcParam] = useQueryParam('toUtc', StringParam)

  const [data, setData] = React.useState<IBroadcastFormRecipientResponseReportRow[]>([])
  const [totalRecords, setTotalRecords] = React.useState(0)
  const [isTruncated, setIsTruncated] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState<any>(null)
  const [loadedCount, setLoadedCount] = React.useState(0)

  React.useEffect(() => {
    let isCancelled = false

    const loadAll = async () => {
      setIsLoading(true)
      setError(null)
      setData([])
      setTotalRecords(0)
      setIsTruncated(false)
      setLoadedCount(0)

      const pageSize = 5000
      const maxRows = 100000

      let page = 1
      let allResults: IBroadcastFormRecipientResponseReportRow[] = []
      let total = 0

      try {
        while (true) {
          // eslint-disable-next-line operator-linebreak
          const result: IPageResult<IBroadcastFormRecipientResponseReportRow> =
            // eslint-disable-next-line no-await-in-loop
            await BroadcastFormReportAPI.getBroadcastFormRecipientResponsesPage({
              fromUtc: fromUtcParam || undefined,
              toUtc: toUtcParam || undefined,
              page,
              pageSize,
            })

          if (isCancelled) return

          if (page === 1) {
            total = result.TotalRecords
            setTotalRecords(total)
          }

          allResults = allResults.concat(result.Results)

          // Update data incrementally so UI can show progress
          if (!isCancelled) {
            setData([...allResults])
            setLoadedCount(allResults.length)
          }

          const hasMore = result.HasMore
          const reachedLimit = allResults.length >= maxRows

          if (!hasMore || reachedLimit) {
            if (reachedLimit && total > maxRows) {
              setIsTruncated(true)
            }
            break
          }

          page += 1
        }
      } catch (err) {
        if (!isCancelled) {
          setError(err)
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false)
        }
      }
    }

    loadAll();

    return () => {
      isCancelled = true
    }
  }, [fromUtcParam, toUtcParam])

  const progressPercentage = totalRecords > 0
    ? Math.min(100, Math.round((loadedCount / totalRecords) * 100))
    : 0

  return {
    data,
    totalRecords,
    isTruncated,
    isLoading,
    error,
    loadedCount,
    progressPercentage,
  }
}
